import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, DollarSign, ChevronRight, RefreshCcw } from "lucide-react";
import { getCostSummary } from "@/lib/api";

/**
 * Cost — Phase 3 spend dashboard.
 *
 * Pulls /api/cost-summary and surfaces three layers of detail:
 *   1. Hero totals (grand + per-category, with window switcher)
 *   2. Storyboards table (each scene-version that was ever rendered)
 *   3. Auto-video jobs table (current + archived renders)
 *
 * Numbers are derived from real recorded provider costs persisted at
 * generation time — fall back to cost_table.py rates only when a
 * legacy record didn't store the price.
 */
const WINDOWS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "all", label: "All time" },
];

export default function Cost() {
  const [window, setWindow] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getCostSummary(window);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load cost summary");
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};

  return (
    <main
      data-testid="cost-page"
      className="flex-1 bg-black min-h-screen text-white"
    >
      <header className="border-b border-zinc-900 px-8 py-6 flex items-baseline justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-mono text-[10px] uppercase tracking-[0.4em] text-yellow-400 mb-2">
            // phase 3 · cost dashboard
          </h1>
          <p className="text-3xl font-bold tracking-tight font-display">Spend</p>
          <p className="text-xs text-zinc-400 mt-2 max-w-xl leading-relaxed">
            Actual dollars billed for every take, render, thumbnail, and kit you've generated. Numbers come from provider receipts at the time of render — switch the window to see your rolling burn rate.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                data-testid={`cost-window-${w.id}`}
                onClick={() => setWindow(w.id)}
                className={
                  `px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ` +
                  (w.id === window
                    ? "bg-yellow-400 text-black font-bold"
                    : "text-zinc-300 hover:text-yellow-300 hover:bg-zinc-950")
                }
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="cost-refresh"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-800 hover:border-yellow-400/60 text-zinc-300 hover:text-yellow-300 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
            title="Refresh"
          >
            {loading
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCcw className="w-3 h-3" />}
            refresh
          </button>
        </div>
      </header>

      <div className="px-8 py-8 space-y-10 max-w-6xl">
        {loading && !data ? (
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-[0.3em]">
            [recon] tallying spend<span className="blink">_</span>
          </div>
        ) : (
          <>
            <TotalsGrid totals={totals} />
            <StoryboardsTable rows={data?.storyboards || []} />
            <AutoVideosTable rows={data?.auto_videos || []} />
          </>
        )}
      </div>
    </main>
  );
}

function TotalsGrid({ totals }) {
  const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;
  const tiles = [
    {
      id: "grand",
      label: "Grand total",
      value: fmt(totals.grand_usd),
      tone: "yellow",
      testid: "cost-total-grand",
    },
    {
      id: "storyboards",
      label: "Storyboards",
      value: fmt(totals.storyboards_usd),
      tone: "emerald",
      testid: "cost-total-storyboards",
    },
    {
      id: "auto-videos",
      label: "Auto-videos",
      value: fmt(totals.auto_videos_usd),
      tone: "cyan",
      testid: "cost-total-auto-videos",
    },
    {
      id: "thumbnails",
      label: "Thumbnails",
      value: fmt(totals.thumbnails_usd),
      tone: "zinc",
      testid: "cost-total-thumbnails",
    },
    {
      id: "channel-kits",
      label: "Channel kits",
      value: fmt(totals.channel_kits_usd),
      tone: "zinc",
      testid: "cost-total-channel-kits",
    },
  ];
  return (
    <div
      data-testid="cost-totals-grid"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
    >
      {tiles.map((t) => (
        <TotalCard key={t.id} {...t} />
      ))}
    </div>
  );
}

function TotalCard({ label, value, tone, testid }) {
  const toneClasses = {
    yellow: "border-yellow-400/60 bg-yellow-400/5",
    emerald: "border-emerald-400/40 bg-emerald-400/[0.04]",
    cyan: "border-cyan-400/40 bg-cyan-400/[0.04]",
    zinc: "border-zinc-800 bg-zinc-950",
  };
  const accent = {
    yellow: "text-yellow-300",
    emerald: "text-emerald-300",
    cyan: "text-cyan-300",
    zinc: "text-zinc-200",
  };
  return (
    <div
      data-testid={testid}
      className={`border ${toneClasses[tone] || toneClasses.zinc} p-4 rounded-sm`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${accent[tone] || accent.zinc}`}>
        {value}
      </div>
    </div>
  );
}

function StoryboardsTable({ rows }) {
  const totalVersions = useMemo(
    () => rows.reduce((acc, r) => acc + (r.versions?.length || 0), 0),
    [rows],
  );
  return (
    <section data-testid="cost-storyboards-section" className="space-y-3">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold">
          // storyboards
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {rows.length} storyboard{rows.length === 1 ? "" : "s"} · {totalVersions} take{totalVersions === 1 ? "" : "s"}
        </span>
      </header>
      {rows.length === 0 ? (
        <div className="border border-zinc-900 rounded-sm p-6 text-xs font-mono text-zinc-500 uppercase tracking-[0.25em]">
          // no storyboard renders in this window
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <StoryboardRow key={r.storyboard_id} row={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function StoryboardRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`cost-storyboard-row-${row.storyboard_id}`}
      className="border border-zinc-900 rounded-sm bg-zinc-950"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/60 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight
            className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <div className="text-left min-w-0">
            <div className="text-sm text-white truncate font-bold">
              {row.video_name || row.storyboard_id}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">
              {row.scene_count} scene{row.scene_count === 1 ? "" : "s"} · {row.versions?.length || 0} take{row.versions?.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-base text-yellow-300 font-bold tabular-nums">
            ${Number(row.total_usd || 0).toFixed(2)}
          </span>
          <Link
            to={`/storyboards/${row.storyboard_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 border border-cyan-400/40 hover:border-cyan-300 px-2 py-1 rounded-sm transition-colors"
            data-testid={`cost-storyboard-open-${row.storyboard_id}`}
          >
            open
          </Link>
        </div>
      </button>
      {open && (
        <div className="border-t border-zinc-900 p-4 space-y-1.5">
          {(row.versions || []).length === 0 ? (
            <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.25em]">
              no takes recorded
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                  <th className="text-left py-1.5">Scene</th>
                  <th className="text-left py-1.5">Take</th>
                  <th className="text-left py-1.5">Provider</th>
                  <th className="text-left py-1.5">When</th>
                  <th className="text-right py-1.5">Cost</th>
                </tr>
              </thead>
              <tbody>
                {row.versions.map((v) => (
                  <tr
                    key={`${v.scene_idx}-${v.version_id}`}
                    data-testid={`cost-take-row-${row.storyboard_id}-${v.version_id}`}
                    className="border-t border-zinc-900"
                  >
                    <td className="py-1.5 text-zinc-300">#{(v.scene_idx ?? 0) + 1}</td>
                    <td className="py-1.5">
                      {v.approved ? (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400/20 border border-emerald-400/60 text-emerald-200 px-1.5 py-0.5 rounded-sm">
                          ★ approved
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                          take
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-zinc-400 font-mono text-[10px]">
                      {v.provider || "—"}
                    </td>
                    <td className="py-1.5 text-zinc-500 font-mono text-[10px]">
                      {formatWhen(v.created_at)}
                    </td>
                    <td className="py-1.5 text-right text-yellow-200 tabular-nums font-bold">
                      ${Number(v.cost_usd || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function AutoVideosTable({ rows }) {
  return (
    <section data-testid="cost-auto-videos-section" className="space-y-3">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold">
          // auto-video renders
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {rows.length} job{rows.length === 1 ? "" : "s"}
        </span>
      </header>
      {rows.length === 0 ? (
        <div className="border border-zinc-900 rounded-sm p-6 text-xs font-mono text-zinc-500 uppercase tracking-[0.25em]">
          // no auto-video renders in this window
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <AutoVideoRow key={r.job_id} row={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function AutoVideoRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`cost-auto-video-row-${row.job_id}`}
      className="border border-zinc-900 rounded-sm bg-zinc-950"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/60 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight
            className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <div className="text-left min-w-0">
            <div className="text-sm text-white truncate font-bold">
              {row.video_name || row.job_id}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">
              {row.renders?.length || 0} render{row.renders?.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-base text-cyan-300 font-bold tabular-nums">
            ${Number(row.total_usd || 0).toFixed(2)}
          </span>
          <Link
            to={`/auto-video/${row.job_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 border border-cyan-400/40 hover:border-cyan-300 px-2 py-1 rounded-sm transition-colors"
            data-testid={`cost-auto-video-open-${row.job_id}`}
          >
            open
          </Link>
        </div>
      </button>
      {open && (
        <div className="border-t border-zinc-900 p-4 space-y-1.5">
          <table className="w-full text-xs">
            <thead>
              <tr className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                <th className="text-left py-1.5">Provider</th>
                <th className="text-left py-1.5">Scenes</th>
                <th className="text-left py-1.5">Fallbacks</th>
                <th className="text-left py-1.5">When</th>
                <th className="text-right py-1.5">Cost</th>
              </tr>
            </thead>
            <tbody>
              {(row.renders || []).map((r, i) => (
                <tr
                  key={`${row.job_id}-${i}`}
                  className="border-t border-zinc-900"
                >
                  <td className="py-1.5 text-zinc-400 font-mono text-[10px]">
                    {r.provider || "—"}
                    {r.is_current && (
                      <span className="ml-2 text-emerald-300">· current</span>
                    )}
                  </td>
                  <td className="py-1.5 text-zinc-300">{r.scenes ?? "—"}</td>
                  <td className="py-1.5 text-zinc-300">{r.fallback_count ?? 0}</td>
                  <td className="py-1.5 text-zinc-500 font-mono text-[10px]">
                    {formatWhen(r.completed_at)}
                  </td>
                  <td className="py-1.5 text-right text-cyan-200 tabular-nums font-bold">
                    ${Number(r.cost_usd || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}
